import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { AppSettingsState, setHome } from 'store/AppSettingsSlice'

export const useSectionsScrolling = (): {
  scrollToTopAndShowHeader: () => void
  scrollToSection: (index: number) => void
  scrollRef: MutableRefObject<HTMLElement | null>
  sectionsRef: MutableRefObject<(HTMLElement | null)[]>
  shouldShowHeader: boolean
  currentSectionIndex: number
} => {
  const sectionsRef = useRef<(HTMLElement | null)[]>([])
  const [top, setTop] = useState(sectionsRef.current[0]?.scrollTop ?? 0)
  const [shouldShowHeader, setShouldShowHeader] = useState<boolean>(true)
  const [clientHeight, setClientHeight] = useState(0)
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)

  const scrollToSection = (index: number) => {
    sectionsRef.current[index]?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollToTopAndShowHeader = () => {
    sectionsRef.current[0]?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const container = scrollRef.current as any
    if (container === undefined || container === null) return

    let timeout: any

    const handleScroll = () => {
      if (timeout) {
        clearTimeout(timeout)
      }

      timeout = setTimeout(() => {
        // Should not do anything it not scrollable
        if (container.clientHeight !== clientHeight) {
          // Client height might changed when show/hide happened
          // We need to wait until client height is stable
          setClientHeight(container.clientHeight)
          setTop(container.scrollTop)
          return
        }

        const scrollDistance = container.scrollTop - top
        if (scrollDistance > 1 || scrollDistance < -1) {
          setShouldShowHeader(scrollDistance < 0)
        }
        setTop(container.scrollTop)
        if (sectionsRef.current) {
          const sectionIndex = sectionsRef.current.findIndex((section) => {
            if (!section) return false
            const sectionTop = section.offsetTop - container.offsetTop
            const height = section.clientHeight
            return (
              container.scrollTop >= sectionTop - 32 && // 32 is padding size
              container.scrollTop <= sectionTop + height - 32
            )
          })
          if (sectionIndex !== currentSectionIndex) {
            setCurrentSectionIndex(sectionIndex)
          }
        }
      }, 50)
    }

    handleScroll()
    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [scrollRef, top]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    scrollToTopAndShowHeader,
    scrollToSection,
    scrollRef,
    sectionsRef,
    shouldShowHeader,
    currentSectionIndex,
  }
}

export const useWindowDimensions = () => {
  const getWindowDimensions = () => {
    const { innerWidth: windowWidth, innerHeight: windowHeight } = window
    return {
      windowWidth,
      windowHeight,
    }
  }

  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(),
  )

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

export const useAppSettings = () => {
  const dispatch = useDispatch()
  const appSettings = useSelector<RootState>(
    (state) => state.appSettings,
  ) as AppSettingsState

  const _setHome = (home: string) => {
    dispatch(setHome(home))
  }

  return {
    appSettings,
    setHome: _setHome,
  }
}
